/* Which screen the sign-in guard shows in place of an app (shell/SessionGate.tsx).
 *
 * In order: nothing until MSAL has settled; sign-in, for a visitor with no
 * account; the second-factor step, for an External ID sign-in that has not
 * passed it this session; the tenant choice, for somebody offered more than
 * one with none chosen; then the app. The API refuses a caller's data until
 * each is met (app/second_factor.py, 15 section 2.2), so an app shown earlier
 * would only fetch refusals.
 *
 * An app that keeps its work on the device (`local`) is shown without a
 * session rather than sent to sign-in, because only syncing needs one (`02` ->
 * Offline work is kept on the phone, and the token is not).
 */
import type { StepStatus } from "../api/secondFactor";
import type { Me } from "../api/types";
import { needsChoice } from "./tenantChoice";

export type GateView = "wait" | "signIn" | "local" | "stepError" | "step" | "choose" | "app";

export interface GateState {
  /* MSAL has not settled. */
  checking: boolean;
  /* MSAL holds an account for this tab. */
  signedIn: boolean;
  /* The app keeps its work on the device and runs without a session. */
  local: boolean;
  /* The session is External ID's, whose sign-ins take Arborcision's second
   * factor (02 -> A local account signs in with a password and Arborcision's
   * second factor). */
  external: boolean;
  /* The step's status as read, or what stands in its place while it is read.
   * `unreachable` is a failure with no answer from the API: no network. */
  step: { pending: boolean; failed: boolean; unreachable: boolean; status: StepStatus | undefined };
  me: Pick<Me, "tenants" | "active_tenant_id"> | undefined;
}

/* The notice the "local" view shows above the app: an account the API cannot
 * be reached to check; no account, with a sign-in offered; or no account and
 * no network, where sign-in cannot work and is not offered. */
export type LocalNotice = "unreachable" | "signedOut" | "signedOutOffline";

export function localNotice(signedIn: boolean, network: boolean): LocalNotice {
  if (signedIn) return "unreachable";
  return network ? "signedOut" : "signedOutOffline";
}

export function gateView(state: GateState): GateView {
  if (state.checking) return "wait";
  if (!state.signedIn) return state.local ? "local" : "signIn";
  if (state.external) {
    if (state.step.failed) return state.local && state.step.unreachable ? "local" : "stepError";
    if (state.step.pending || !state.step.status) return "wait";
    if (state.step.status.required && !state.step.status.passed) return "step";
  }
  if (state.me && needsChoice(state.me)) return "choose";
  return "app";
}
